import { Textarea } from "@/src/components/ui/textarea";
import { Input } from "@/src/components/ui/input";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { fetch, SuccessResponse } from "@/src/lib/fetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";
import { PatchLocationValues, PatchLocationSchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { Location, Town } from "../model";
import { Button } from "@/src/components/ui/button";
import { extractFromMultiName } from "../utils";
import { Yacht } from "../../Yachts/model";
import { YachtsSection } from "../components/YachtsSection";
import { FormCheckbox } from "@/src/components/ui/form-checkbox";
import { FormErrors } from "../components/FormErrors";
import { MergeLocationsButton } from "../components/MergeLocationsButton";
import { useEffect } from "react";

export async function locationLoader({ params }: { params: { locationId: string } }) {
  const response = await fetch<SuccessResponse<Location>>(`/api/locations/locations/${params.locationId}`);
  return { defaultValues: response.data, locationId: params.locationId };
}

export function EditLocation() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaultValues, locationId } = useLoaderData() as {
    defaultValues: Location;
    locationId: string;
  };

  const methods = useForm<PatchLocationValues>({
    resolver: zodResolver(PatchLocationSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [locationId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const loadTowns = async (q: string) => {
    const data = await fetch<{ data: Town[] }>(
      `/api/locations/towns?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const loadYachts = async () => {
    const data = await fetch<{ data: Yacht[] }>(`/api/locations/locations/${locationId}/yachts`);
    return data.data;
  };

  const onSubmit = async (data: PatchLocationValues) => {
    try {
      await fetch(`/api/locations/locations/${locationId}`, "PATCH", data);
      toast({ title: "Success", description: "Location updated successfully" });
      navigate(`/locations/${locationId}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update Location", variant: "destructive" });
    }
  };

  const deleteLocation = async () => {
    if (window.confirm("Are you sure you want to delete this location?")) {
      try {
        await fetch(`/api/locations/locations/${locationId}`, "DELETE");
        toast({ title: "Success", description: "Location deleted successfully" });
        navigate("/locations");
      } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl shadow-md bg-white p-4">
        <h2 className="text-lg font-semibold">Edit Location - {extractFromMultiName(defaultValues.names)}</h2>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
            <div>
              <Label>Names</Label>
              <Textarea {...register("names")} placeholder="Enter names, one per line" />
            </div>

            <div>
              <Label>Latitude</Label>
              <Input {...register("latitude", { valueAsNumber: true })} placeholder="Latitude" />
            </div>

            <div>
              <Label>Longitude</Label>
              <Input {...register("longitude", { valueAsNumber: true })} placeholder="Longitude" />
            </div>

            <div>
              <Label>Address</Label>
              <Textarea {...register("address")} placeholder="Address" />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea {...register("notes")} placeholder="Notes" />
            </div>

            <RelationSingleInput name="town" label="Town" loadOptions={loadTowns} />

            <FormErrors errors={errors} />

            <Button type="submit">Save</Button>
            <Button type="button" variant="destructive" onClick={deleteLocation} className="ml-2">
              Delete
            </Button>
            <MergeLocationsButton source={locationId} />
          </form>
        </FormProvider>
      </div>
      <YachtsSection loadYachts={loadYachts} />
    </div>
  );
}
